import React from "react";
import PropTypes from "prop-types";
import Grid from "./Grid";
import Carousel from "./Carousel";
import ReactGA from "react-ga4";
import HeaderMoreLink from "arena/components/HeaderMoreLink";
import ShelfTitleLink from "arena/components/ShelfTitleLink/ShelfTitleLink";
import HomepageTitle from "arena/components/HomepageTitle";
import Box from "@mui/material/Box";
import { useQuery } from "@apollo/client";
import { useArena } from "core/components/ArenaProvider";
import { ArenaPostsQuery } from "arena/queries";

const components = {
  CAROUSEL: Carousel,
  GRID: Grid,
};

/**
 * Список в стиле полки контента
 */
export default function ArenaPosts({
  title,
  view,
  variant,
  limit = 12,
  tagIds,
  productIds,
  rightholderIds,
  loadMore,
  itemsPerRow,
  moreUrlHref,
}) {
  const arena = useArena();
  itemsPerRow = itemsPerRow || arena.defaultRowSize;

  const variables = {
    rowsPerPage: limit,
    tagIds,
    productIds,
  };

  if (rightholderIds?.length) {
    variables.rightholderIds = rightholderIds;
  }

  const { data, loading, fetchMore } = useQuery(ArenaPostsQuery, {
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    notifyOnNetworkStatusChange: false,
    variables,
  });

  const count = data?.posts?.count || 0;
  const nodes = data?.posts?.nodes || [];

  if (!count) return null;

  let moreLink = moreUrlHref?.trim() || null;

  if (!moreLink && !loadMore) {
    if (count > limit) {
      const searchParams = new URLSearchParams();
      if (tagIds) {
        tagIds.forEach((tagId) => {
          searchParams.append("tagId", tagId);
        });
      }
      moreLink = `/posts?${searchParams.toString()}`;
    }
  }

  const hasMore = nodes.length < count;
  const nextPage = Math.floor(nodes.length / limit); // Для page-based пагинации (page начинается с 0)

  const onLoadMore = () => {
    // Предотвращаем повторные вызовы во время загрузки
    if (loading) {
      return;
    }

    ReactGA.event({
      category: "User",
      action: "Load more posts",
      label: nextPage + " page",
    });

    fetchMore({
      variables: {
        page: nextPage,
      },
    }).catch((error) => {
      console.error("ArenaPosts fetchMore error", error);
      throw error;
    });
  };

  const Component = components[view];

  const sm = Math.max(12 / itemsPerRow, 4);
  const md = Math.max(12 / itemsPerRow, 3);
  const lg = 12 / itemsPerRow;
  const xl = lg;

  let titleElement = title;
  if (moreLink && typeof title === "string") {
    titleElement = <ShelfTitleLink url={moreLink}>{title}</ShelfTitleLink>;
  }

  return (
    <>
      {title ? (
        <HomepageTitle
          title={titleElement}
          action={moreLink && <HeaderMoreLink url={moreLink} />}
        />
      ) : (
        <Box sx={{ mt: 2 }} />
      )}

      <Component
        items={nodes}
        variant={variant}
        xs={6}
        sm={sm}
        md={md}
        lg={lg}
        xl={xl}
        hasMore={loadMore && hasMore}
        onLoadMore={onLoadMore}
        loading={loading}
        nextPage={nextPage}
      />
    </>
  );
}

ArenaPosts.defaultProps = {
  // itemsPerRow: 3,
  view: "CAROUSEL",
  variant: "TITLE_INSIDE",
};

ArenaPosts.propTypes = {
  title: PropTypes.string,
  limit: PropTypes.number,
  itemsPerRow: PropTypes.number,
  tagIds: PropTypes.arrayOf(PropTypes.string),
  productIds: PropTypes.arrayOf(PropTypes.string),
  rightholderIds: PropTypes.arrayOf(PropTypes.string),
  arena: PropTypes.shape({
    id: PropTypes.string.isRequired,
  }),
  view: PropTypes.oneOf(["CAROUSEL", "GRID"]).isRequired,
  variant: PropTypes.oneOf(["TITLE_INSIDE", "TITLE_OUTSIDE"]),
  loadMore: PropTypes.bool,
  moreUrlHref: PropTypes.string,
};
